import { openOrCloseModalSnippets } from "../../../../../../hooks/automation";
import { connect, useSelector } from "react-redux";
import CodeMirror, { basicSetup, EditorView } from "@uiw/react-codemirror";
import { materialDark, materialLight } from "@uiw/codemirror-theme-material";
import { javascript } from "@codemirror/lang-javascript";
import { autocompletion } from "@codemirror/autocomplete";
import React, { useMemo, useRef, useState } from "react";
import { Button, Collapse, Tooltip, Modal } from "antd";
import { ExpandOutlined } from "@ant-design/icons";
import { getNameToSnippets } from "./functions";
import SnippetSections from "../../Snippets/SnippetSection";
import { CODE_HELPER_PLACEHOLDER } from "../../Snippets/codeHelpers";
import { getRecordValueSuffix } from "../../Snippets/recordFieldPath";

const CodeEditor = ({ onChange, value, onBlur }) => {
    const { isDarkMode } = useSelector((state) => state.theme);

    const [code, setCode] = useState(value);
    // O inspector é estreito por natureza (o fluxo continua visível ao lado), e
    // script de dezenas de linhas não se lê nele. Tela cheia é o mesmo
    // documento com a janela inteira — sem tirar o editor de dentro do passo.
    const [isFullScreen, setIsFullScreen] = useState(false);

    const inlineRef = useRef();
    const fullScreenRef = useRef();

    const handleEditorChange = (content) => {
        setCode(content);
        if (onChange) {
            onChange(content);
        }
    };

    // As anotações escrevem no editor visível: com a tela cheia aberta, inserir
    // no editor do painel deixaria a inserção fora de vista. Os dois editores
    // compartilham o mesmo `code`, então o conteúdo continua único.
    const getEditorView = () => {
        const ref = isFullScreen ? fullScreenRef : inlineRef;
        return ref.current ? ref.current.view : null;
    };

    const placeCursor = (editor, position) => {
        const cursorPos = Math.max(0, Math.min(position, editor.state.doc.length));
        editor.dispatch({
            selection: { anchor: cursorPos, head: cursorPos },
        });
        editor.focus();
    };

    // A inserção usa a seleção, não só o cursor: é o que deixa o `VALOR` de uma
    // expressão ser trocado pela anotação clicada em seguida.
    const insertSnippets = (snippet, action = null, step, isLoop = false, record = null) => {
        const editor = getEditorView();
        if (editor) {
            let snippetFinal = getNameToSnippets(step, action, true)
            const { from, to } = editor.state.selection.main;
            // Colchetes em vez de ponto: nome de campo com espaço ou acento
            // (`trigger.Data da coleta`) não é expressão JavaScript válida.
            // O sufixo leva ao valor do campo, que é lista de `{_id, value}`.
            let functionNameWithParentheses =
                `${snippetFinal}[${JSON.stringify(snippet)}]${getRecordValueSuffix(record, true)}`;
            if (isLoop) {
                functionNameWithParentheses = snippet
            }

            const transaction = editor.state.update({
                changes: { from, to, insert: functionNameWithParentheses },
            });
            editor.dispatch(transaction);

            placeCursor(editor, from + functionNameWithParentheses.length);
        }
    }

    const insertCodeHelper = (helper) => {
        const editor = getEditorView();
        if (!editor) return;

        const { from, to } = editor.state.selection.main;
        const text = helper.insert;

        editor.dispatch(editor.state.update({
            changes: { from, to, insert: text },
        }));

        const placeholderAt = text.indexOf(CODE_HELPER_PLACEHOLDER);
        if (placeholderAt >= 0) {
            const max = editor.state.doc.length;
            editor.dispatch({
                selection: {
                    anchor: Math.min(from + placeholderAt, max),
                    head: Math.min(from + placeholderAt + CODE_HELPER_PLACEHOLDER.length, max),
                },
            });
            editor.focus();
        } else {
            placeCursor(editor, from + text.length);
        }
    };

    // `lineWrapping`: numa coluna estreita, linha longa (uma expressão com
    // caminho de campo já passa da largura) só se lia com rolagem horizontal —
    // que esconde o começo da linha enquanto se lê o fim.
    const extensions = useMemo(() => ([
        javascript(),
        basicSetup(),
        autocompletion(),
        EditorView.lineWrapping,
    ]), []);

    const editorProps = {
        theme: isDarkMode ? materialDark : materialLight,
        value: code,
        extensions,
        onChange: handleEditorChange,
        // 100% aqui e altura no contêiner (CSS): assim a área do editor
        // acompanha o espaço disponível em vez de um valor fixo em px.
        height: "100%",
        width: "100%",
        style: { height: "100%" },
    };

    const snippets = (
        <SnippetSections insertSnippets={insertSnippets} insertCodeHelper={insertCodeHelper} isCode />
    );

    // Editor e anotações empilhados: lado a lado (o layout anterior) sobrava
    // menos de 200px para cada um dentro do inspector. As anotações ficam num
    // painel recolhido — são consulta, não a tarefa principal. Em tela cheia há
    // largura para os dois, e aí voltam a ficar lado a lado.
    return (
        <div>
            <div className="ae-field">
                <div className="ae-code__bar">
                    <label className="ae-label ae-code__label">Código</label>
                    <Tooltip title="Editar em tela cheia">
                        <Button
                            size="small"
                            type="text"
                            icon={<ExpandOutlined />}
                            onClick={() => setIsFullScreen(true)}
                        >
                            Expandir
                        </Button>
                    </Tooltip>
                </div>
                <div className="ae-code">
                    <CodeMirror ref={inlineRef} {...editorProps} />
                </div>
            </div>
            <Collapse
                size="small"
                items={[{
                    key: "snippets",
                    label: "Variáveis e funções disponíveis",
                    children: (
                        <div className="ae-code__snippets">
                            {snippets}
                        </div>
                    ),
                }]}
            />
            <Modal
                open={isFullScreen}
                onCancel={() => setIsFullScreen(false)}
                title="Código do Script"
                className="ae-code-modal"
                width="96vw"
                centered
                destroyOnHidden
                styles={{ body: { padding: 0 } }}
                footer={(
                    <Button type="primary" onClick={() => setIsFullScreen(false)}>
                        Concluir
                    </Button>
                )}
            >
                <div className="ae-code-modal__grid">
                    <div className="ae-code-modal__editor">
                        <CodeMirror ref={fullScreenRef} {...editorProps} />
                    </div>
                    <div className="ae-code-modal__side">
                        <div className="ae-code-modal__side-title">Variáveis e funções disponíveis</div>
                        <div className="ae-code-modal__side-body">
                            {snippets}
                        </div>
                    </div>
                </div>
            </Modal>
        </div>
    )
}
const mapStateToProps = ({ automation }) => ({
    actions: automation.actions,
    actionStep: automation.actionStep,
    trigger: automation.trigger,
    snippets: automation.snippets
});

const mapDispatchToProps = (dispatch) => {
    return {
        onOpenOrCloseModalSnippets: ({ action, fieldName, fieldLabel }) => dispatch(openOrCloseModalSnippets({
            action,
            fieldName,
            fieldLabel,
        })),
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(CodeEditor)
